import AddIcon from "@mui/icons-material/Add";
import TranslateIcon from "@mui/icons-material/Translate";
import {
    Chip,
    CircularProgress,
    Dialog,
    Grid,
    Stack
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { createAttributeValue, updateAttributeValue } from "../../services/attributeValueFn";
import { handleOpenSnackBar } from "../../utils";
import { translateText } from "../../utils/translationService";
import { addEditAttributeValuesValidation } from "../../utils/Validation";
import Label from "../Label";
import TextInput from "../TextInputs";
import DialogBody from "./Body";
import DialogActionButtons from "./Footer";
import DialogHeader from "./Header";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialogContent-root": {
        padding: theme.spacing("24px"),
    },
    "& .MuiPaper-root": {
        width: "600px",
        [theme.breakpoints.down("sm")]: {
            margin: 12,
        },
    },
}));

const initialInputValues = {
    label_en: "",
    label_ro: "",
    order: 0,
};

const AddEditAttributeValuesDialog = forwardRef(({ attribute }, ref) => {
    const [open, setOpen] = useState(false);
    const { t } = useTranslation(["catalogue", "common"]);
    const [isLoading, setIsLoading] = useState({
        save: false,
        detail: false,
    });
    const [dialogData, setDialogData] = useState(null);
    const [inputError, setInputError] = useState({});
    const [inputValues, setInputValues] = useState(initialInputValues);

    const { currentLanguage } = useSelector((state) => state.languageReducer);

    useImperativeHandle(ref, () => ({
        openDialog(params) {
            setDialogData(params);
            setOpen(true);
            if (params?.type === "edit") {
                setInputValues({
                    label_ro: params?.data?.label?.ro,
                    label_en: params?.data?.label?.en,
                    order: params?.data?.sortOrder,
                });
            }
        },

        closeDialog() {
            setOpen(false);
        },
    }));

    const handleClose = () => {
        setOpen(false);
        setInputValues(initialInputValues);
        setInputError({});
        setDialogData(null);
    };

    const handleChangeValues = (e) => {
        const { name, value, valueAsNumber, type } = e.target;

        const formattedValue = type === "number" ? valueAsNumber : value;

        setInputValues((prev) => ({ ...prev, [name]: formattedValue }));
        setInputError((prev) => ({ ...prev, [name]: "" }));
    };


    const handleAddValue = () => {
        const valid = addEditAttributeValuesValidation(inputValues, setInputError);
        if (valid) return handleOpenSnackBar("error", t("resolveTheIssues", { ns: "common" }));

        const payload = {
            attributeId: attribute?._id,
            label: {
                en: inputValues.label_en,
                ro: inputValues.label_ro,
            },
            sortOrder: inputValues.order,
        };


        if (dialogData?.type === "add") {
            handleApiCall(createAttributeValue, payload, "");
        } else {
            handleApiCall(updateAttributeValue, payload, dialogData?.data?._id);
        }


    };


    const handleApiCall = async (apiFunction, payload, id) => {
        try {
            setIsLoading((prev) => ({ ...prev, save: true }));
            const response = await apiFunction(id, payload);
            if (response?.data?.status === "success") {
                handleOpenSnackBar("success", response?.data?.message);
                handleClose();
                dialogData?.getData();
            } else {
                handleOpenSnackBar("error", response?.data?.message);
            }
        } catch (error) {
            handleOpenSnackBar("error", t("error.somethingWentWrong", { ns: "common" }));
        } finally {
            setIsLoading((prev) => ({ ...prev, save: false }));
        }
    };



    const headingText = `${t("addAttributeValues")} - ${attribute?.label[currentLanguage]}`;
    const secondaryHeadingText = t("addAttributeValuesInstruction");



    const handleTranslateToEnglish = async (key1, key2) => {
        setIsLoading((prev) => ({ ...prev, translateToEnglish: true }));
        try {
            const [englishText] = await Promise.all([
                translateText(inputValues[key1]),
            ]);
            setInputValues((prev) => ({
                ...prev,
                [key2]: englishText,
            }));
            setInputError((prev) => ({
                ...prev,
                [key2]: "",
            }));
        } catch (error) {
            handleOpenSnackBar("error", t("error.errorWhileTranslating", { ns: "common" }));
        } finally {
            setIsLoading((prev) => ({ ...prev, translateToEnglish: false }));
        }
    };


    return (
        <BootstrapDialog
            onClose={handleClose}
            aria-labelledby="customized-dialog-title"
            open={open}
        >
            <DialogHeader
                title={headingText}
                secondaryHeading={secondaryHeadingText}
                onClose={handleClose}
                icon={<AddIcon />}
            />
            <DialogBody>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Label
                            labelText={t("attributeValueLabelInRomanian")}
                            required
                            htmlFor={"label_ro"}
                        />
                        <TextInput
                            id={"label_ro"}
                            type={"text"}
                            placeholder={t("enterAttributeValueLabelInRomanian")}
                            name={"label_ro"}
                            value={inputValues.label_ro}
                            onChange={handleChangeValues}
                            inputError={Boolean(inputError.label_ro)}
                            helperText={t(inputError.label_ro)}
                            required
                        />
                    </Grid>


                    <Grid item xs={12}>
                        <Stack
                            mb={"5px"}
                            direction={"row"}
                            alignItems={"center"}
                            justifyContent={"space-between"}
                        >
                            <Label
                                required
                                labelText={t("attributeValueLabelInEnglish")}
                                htmlFor={`attribute-name-en`}
                                labelProps={{ marginBottom: 0 }}
                            />
                            <Chip
                                label={t("translateToEnglish", { ns: "common" })}
                                size="small"
                                onClick={() =>
                                    handleTranslateToEnglish("label_ro", "label_en")
                                }
                                icon={
                                    isLoading.translateToEnglish ? (
                                        <CircularProgress size={16} color="inherit" />
                                    ) : (
                                        <TranslateIcon />
                                    )
                                }
                                disabled={isLoading.translateToEnglish}
                            />
                        </Stack>
                        <TextInput
                            id={"label_en"}
                            type={"text"}
                            placeholder={t("enterAttributeValueLabelInEnglish")}
                            name={"label_en"}
                            value={inputValues.label_en}
                            onChange={handleChangeValues}
                            inputError={Boolean(inputError.label_en)}
                            helperText={t(inputError.label_en)}
                            required
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Label labelText={t("attributeValueOrder")} required htmlFor={"order"} />
                        <TextInput
                            id={"attribute-value-order"}
                            type={"number"}
                            placeholder={t("enterAttributeValueOrder")}
                            name={"order"}
                            value={inputValues.order}
                            onChange={handleChangeValues}
                            inputError={Boolean(inputError.order)}
                            helperText={t(inputError.order)}
                            required
                        />
                    </Grid>


                </Grid>
            </DialogBody>

            <DialogActionButtons
                onCancel={handleClose}
                cancelText={t("cancel")}
                confirmText={dialogData?.type === "add" ? t("add", { ns: "common" }) : t("edit", { ns: "common" })}
                showCancelButton={true}
                onConfirm={handleAddValue}
                isConfirmBtnDisable={isLoading.save}
            />
        </BootstrapDialog>
    );
});

export default AddEditAttributeValuesDialog;
